import { prepareHtmlForCapture } from "src/pages/ad-studio/html-ad-editor/capture-html-preview";
import { HOSTED_FONT_STYLESHEET_IMPORT } from "src/pages/ad-studio/html-ad-editor/hosted-font-stylesheet-urls";

function readCanvasDimension(
  root: HTMLElement,
  property: "width" | "height",
): number {
  const value = root.style.getPropertyValue(property).trim();
  if (!/^\d+(?:\.\d+)?px$/.test(value)) {
    throw new Error(
      "The ad needs a fixed canvas size before it can be exported.",
    );
  }
  const dimension = Number.parseFloat(value);
  if (!Number.isFinite(dimension) || dimension <= 0) {
    throw new Error(
      "The ad needs a valid canvas size before it can be exported.",
    );
  }
  return Math.ceil(dimension);
}

export async function captureHostedFontContent(args: {
  content: string;
  fontRules: string[];
  stylesheetUrls: string[];
  componentAssets: Record<string, string> | null;
  apiBase: string;
  assetId: string;
}): Promise<Blob> {
  const { body } = new DOMParser().parseFromString(args.content, "text/html");
  const root = body.firstElementChild;
  if (!(root instanceof HTMLElement) || body.children.length !== 1) {
    throw new Error(
      "Adobe Fonts are supported in static HTML ads. Choose a different font for this ad.",
    );
  }
  const prepared = await prepareHtmlForCapture({
    htmlString: args.content,
    componentAssets: args.componentAssets,
    apiBase: args.apiBase,
  });
  const response = await fetch(
    `${args.apiBase}/agents/ads/${encodeURIComponent(args.assetId)}/render-image`,
    {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        html: prepared.outerHTML,
        width: readCanvasDimension(root, "width"),
        height: readCanvasDimension(root, "height"),
        componentAssets: args.componentAssets ?? {},
        stylesheetUrls: args.stylesheetUrls,
        fontCss: args.fontRules
          .filter((rule) => !HOSTED_FONT_STYLESHEET_IMPORT.test(rule))
          .join("\n"),
      }),
    },
  );
  if (!response.ok) {
    throw new Error(
      "Couldn't render the ad with Adobe Fonts. Check the font connection and try again.",
    );
  }
  if (response.headers.get("content-type")?.split(";")[0] !== "image/png") {
    throw new Error("Couldn't download the rendered ad. Try again.");
  }
  return response.blob();
}
